import { useContext, useEffect } from 'react';
import { SebastianStatus } from '~/constants/common/SebastianStatus';
import { useSebastianStatus } from '~/context/common/SebastianContext';
import UserVehicleContext, {
  useVehiclePaymentTerms
} from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import { useVehiclePrice } from '~/hooks/vdp/useVehiclePrice';
import { generateErrorLogger } from '~/utilities/loggers';
import deriveVehicleDescription from '~/utilities/vdp/deriveVehicleDescription';

const logSetPageContextError = generateErrorLogger(
  'useSendVdpSebastianPageContext.setPageContext'
);
const logSetPageContextCleanupError = generateErrorLogger(
  'useSendVdpSebastianPageContext.setPageContext.cleanup'
);

export default function useSendVdpSebastianPageContext() {
  const sebastianStatus = useSebastianStatus();
  const { make, mileage, model, offering, trim, vehicleId, year } =
    useGetVehicleDetails();
  const { incentivizedPrice } = useVehiclePaymentTerms();
  const { vehicleAvailability } = useContext(UserVehicleContext);
  const { finalPrice: displayedPrice } = useVehiclePrice();
  const {
    isReservedByOtherAndSubscribed,
    isReservedByUserAndKicked,
    type: ctaType
  } = usePrimaryCtaButtonState();
  const {
    isSold,
    notAvailableInCA,
    outOfMarket,
    outOfMarketNewCar,
    purchasePending,
    stopSaleRecall
  } = useAlertStatus();
  const isInExclusivePreview = useIsInExclusivePreview();

  const isOutOfArea = notAvailableInCA || outOfMarket || outOfMarketNewCar;
  const description = vehicleAvailability.isLoaded
    ? deriveVehicleDescription({
        ctaType,
        isInExclusivePreview,
        isOutOfArea,
        isReservedByOtherAndSubscribed,
        isReservedByUserAndKicked,
        isSold,
        purchasePending,
        stopSaleRecall
      })
    : undefined;

  useEffect(() => {
    if (sebastianStatus !== SebastianStatus.Ready || !vehicleId) return;

    try {
      window.CarvanaChatUi?.setPageContext?.(
        {
          surface: 'VDP',
          surface_description:
            'Customer is viewing a vehicle detail page for a specific vehicle'
        },
        {
          ...(description && { description }),
          ...(displayedPrice && { displayedPrice }),
          ...(incentivizedPrice && { incentivizedPrice }),
          ...(make && { make }),
          ...(mileage != null && { mileage }),
          ...(model && { model }),
          ...(offering && { offering }),
          ...(trim && { trim }),
          vehicleId,
          ...(year && { year })
        }
      );
    } catch (err) {
      logSetPageContextError(err);
    }

    return () => {
      try {
        window.CarvanaChatUi?.setPageContext?.(undefined, undefined);
      } catch (err) {
        logSetPageContextCleanupError(err);
      }
    };
  }, [
    description,
    displayedPrice,
    incentivizedPrice,
    make,
    mileage,
    model,
    offering,
    sebastianStatus,
    trim,
    vehicleId,
    year
  ]);
}
